if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        // Store original disabled state for quantity_restricted products
        if (this.dataset.quantityRestricted === 'true' && this.submitBtn) {
          const originalDisabled = this.submitBtn.disabled || this.submitBtn.hasAttribute('disabled');
          this.submitBtn.setAttribute('data-original-disabled', originalDisabled ? 'true' : 'false');
        }

        this.form.addEventListener('submit', this.handleSubmit.bind(this));

        // Check if item is in cart and update button state for quantity_restricted products
        if (this.dataset.quantityRestricted === 'true') {
          this.checkCartAndUpdateButton();
          // Listen for cart updates
          const cartUpdateHandler = () => this.checkCartAndUpdateButton();
          document.addEventListener('cart:updated', cartUpdateHandler);
          document.addEventListener('cart:refresh', cartUpdateHandler);
          document.addEventListener('on:line-item:change', cartUpdateHandler);
          // Listen for variant changes
          document.addEventListener('on:variant:change', () => {
            // Small delay to ensure variant ID is updated
            setTimeout(() => this.checkCartAndUpdateButton(), 100);
          });
          // Also listen for form changes (variant input changes)
          this.form.addEventListener('change', (e) => {
            if (e.target.name === 'id') {
              setTimeout(() => this.checkCartAndUpdateButton(), 50);
            }
          });
        }
      }
    }

    async checkCartAndUpdateButton() {
      try {
        // Use cached cart data from MarketCartUtils to avoid extra fetches
        let cartData;
        if (window.MarketCartUtils) {
          // First try event cache (no fetch needed)
          cartData = window.MarketCartUtils.getEventCartData();
          if (!cartData) {
            // Fall back to cached fetch
            cartData = await window.MarketCartUtils.getMarketAwareCartData();
          }
        } else {
          const cartUrl = '/cart.js';
          const response = await fetch(cartUrl);
          if (!response.ok) return;
          cartData = await response.json();
        }

        if (!cartData) return;

        const variantId = parseInt(this.form.querySelector('[name="id"]').value);
        const productId = this.dataset.productId ? parseInt(this.dataset.productId) : null;
        
        // Find cart item with matching variant - check for both quantity_restricted AND upsell items
        // Both types should prevent adding the same variant again
        const cartItem = cartData.items.find(item => {
          if (item.variant_id !== variantId || !item.properties) return false;
          const isQuantityRestricted = item.properties._quantity_restricted === 'true';
          const isUpsell = item.properties._upsell_item === 'true';
          const isCheckoutBlockUpsell = item.properties._checkoutblocks && String(item.properties._checkoutblocks).includes('advanced-upsell');
          return isQuantityRestricted || isUpsell || isCheckoutBlockUpsell;
        });
        
        if (cartItem) {
          // Item is in cart - grey out button
          this.submitBtn.setAttribute('data-quantity-restricted-in-cart', 'true');
          this.submitBtn.disabled = true;
          this.submitBtn.setAttribute('aria-disabled', 'true');
          this.submitBtn.classList.add('is-disabled');
          this.submitBtn.style.opacity = '0.5';
          this.submitBtn.style.cursor = 'not-allowed';
        } else {
          // Item not in cart - remove quantity restriction disabled state
          const wasDisabledByQuantityRestriction = this.submitBtn.hasAttribute('data-quantity-restricted-in-cart');
          this.submitBtn.removeAttribute('data-quantity-restricted-in-cart');
          
          // Only re-enable if it was disabled by us due to quantity restriction
          if (wasDisabledByQuantityRestriction) {
            // Check original disabled state - if it was disabled for other reasons (sold out, etc), keep it disabled
            const originalDisabled = this.submitBtn.getAttribute('data-original-disabled');
            if (originalDisabled !== 'true') {
              this.submitBtn.disabled = false;
              this.submitBtn.removeAttribute('aria-disabled');
            }
          }
          this.submitBtn.classList.remove('is-disabled');
          this.submitBtn.style.opacity = '';
          this.submitBtn.style.cursor = '';
        }
      } catch (error) {
        console.error('Error checking cart for quantity restricted product:', error);
      }
    }

    /**
     * Tracked stock level for a variant, from the map rendered by
     * product-buy-buttons.liquid. Returns null for variants that allow
     * overselling (untracked or `continue` policy) - they aren't in the map.
     * @param {string|number} variantId - Variant ID.
     * @returns {number|null} Available quantity, or null if not enforced.
     */
    getVariantInventory(variantId) {
      if (!this.dataset.variantInventory) return null;
      try {
        if (!this.variantInventoryMap) {
          this.variantInventoryMap = JSON.parse(this.dataset.variantInventory);
        }
        const available = this.variantInventoryMap[String(variantId)];
        return typeof available === 'number' ? available : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      // Prevent double submission
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;
      if (this.isSubmitting) return;

      // Check if cart is busy with another operation
      if (window.MarketCartUtils && window.MarketCartUtils.isCartBusy()) {
        return;
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();
      let addFailed = false;

      // Lock the button immediately to prevent double clicks
      this.isSubmitting = true;
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity') || 1;

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Build properties object
      const properties = {};
      if (this.dataset.quantityRestricted === 'true') {
        properties._quantity_restricted = 'true';
        properties._restricted_id = variantId;
      }

      // Get any other properties from form
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && key.endsWith(']')) {
          const propName = key.slice(11, -1);
          properties[propName] = value;
        }
      }

      // Optimistic badge bump + cart icon shake are handled centrally by
      // MarketCartUtils.addToCart (see market-cart-utils.js).

      try {
        // Get old cart data from cache ONLY (no fetch) - just for comparison
        let oldCartData = null;
        if (window.MarketCartUtils) {
          oldCartData = window.MarketCartUtils.getEventCartData();
          // Don't fetch if not cached - it's just for comparison and not critical
        }

        // Use centralized add to cart with queue management
        let data;
        if (window.MarketCartUtils && window.MarketCartUtils.addToCart) {
          data = await window.MarketCartUtils.addToCart(variantId, quantity, properties, {
            sections: sections,
            sections_url: window.location.pathname + window.location.search,
            inventoryAvailable: this.getVariantInventory(variantId)
          });
        } else {
          // Fallback to direct fetch
          formData.append('sections_url', window.location.pathname + window.location.search);
          formData.append('sections', sections);

          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });

          // Safe JSON parsing
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('text/html')) {
            throw new Error(response.status === 429 ? 'Too many requests. Please wait a moment.' : 'Server error');
          }

          data = await response.json();
          if (!response.ok) {
            let error = typeof data.description === 'string' ? data.description : data.message;
            if (data.errors && typeof data.errors === 'object') {
              error = Object.entries(data.errors).map((item) => item[1].join(', '));
            }
            throw new Error(error || response.status);
          }
        }

        // Handle errors in response
        if (data.status && data.status !== 200) {
          let error = typeof data.description === 'string' ? data.description : data.message;
          if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors).map((item) => item[1].join(', '));
          }
          this.setErrorMsgState(error);
          throw new Error(error);
        }

        // Update cart icon count
        ProductForm.updateCartIcon(data);

        // If item was added from Quick Add drawer, show "Added to cart" message
        const quickAddDrawer = this.closest('quick-add-drawer');
        if (quickAddDrawer) quickAddDrawer.addedToCart();

        const onCartPage = window.location.pathname === theme.routes.cart;
        if (this.cartDrawer && !onCartPage) {
          const scheduleUpdate = window.scheduleCartDrawerAfterAdd || ((drawer, payload, opts) => {
            return drawer.renderContents(payload, opts.openDrawer);
          });
          scheduleUpdate(this.cartDrawer, data, { openDrawer: !quickAddDrawer });
        } else if (onCartPage) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            if (cartItems.dataset.empty === 'true') {
              window.location.reload();
            } else {
              cartItems.refresh();
            }
          }
        }

        // Fetch cart for event listeners without blocking drawer section render.
        (async () => {
          let newCartData;
          if (window.MarketCartUtils) {
            window.MarketCartUtils.clearCartCache();
            window.MarketCartUtils.eventCartData = null;
            newCartData = await window.MarketCartUtils.getMarketAwareCartData();
            if (newCartData) {
              window.MarketCartUtils.setEventCartData(newCartData);
            }
          } else {
            const newCartResponse = await fetch(`${theme.routes.cart}.js`);
            if (newCartResponse.ok) {
              newCartData = await newCartResponse.json();
            }
          }

          if (!newCartData) return;

          const itemInOldCart = oldCartData?.items?.find(
            (item) => item.variant_id === data.variant_id
          );

          if (itemInOldCart) {
            document.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: data.quantity
              }
            }));
          } else {
            document.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id
              }
            }));
          }

          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              action: itemInOldCart ? 'change' : 'add'
            }
          }));
        })();

        // Update button state for quantity_restricted products
        if (this.dataset.quantityRestricted === 'true') {
          setTimeout(() => this.checkCartAndUpdateButton(), 200);
        }
      } catch (error) {
        let errorMessage = error.message || 'Failed to add to cart';
        if (window.MarketCartUtils && typeof window.MarketCartUtils.getCustomerFacingCartError === 'function') {
          errorMessage = window.MarketCartUtils.getCustomerFacingCartError(error) || errorMessage;
        }
        addFailed = true;
        this.setErrorMsgState(errorMessage);

        const cartWideValidation = window.MarketCartUtils
          && typeof window.MarketCartUtils.isCartWideCheckoutValidationError === 'function'
          && window.MarketCartUtils.isCartWideCheckoutValidationError(error, errorMessage);

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: errorMessage
          }
        }));

        // Nothing was sent to the server for an inventory block, so the drawer
        // is already in sync - refreshing it would just cost a request.
        // Checkout validation (422) also leaves the cart unchanged — skip refresh.
        if (this.cartDrawer
          && error.name !== 'InventoryLimitError'
          && !cartWideValidation) {
          this.cartDrawer.refresh(true);
        }
      } finally {
        this.isSubmitting = false;
        // Don't flash the success state underneath an error message.
        if (!addFailed) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        const resetDelay = addFailed ? 0 : 1400;
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, resetDelay);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    /**
     * Animate every visible cart icon in the header using the Web Animations API.
     * Iterates all duplicate `#cart-icon` elements (multi-market header) and
     * skips ones that aren't actually visible.
     */
    static shakeCartIcon() {
      window.MarketCartUtils?.shakeCartIcon();
    }

    static updateCartIcon(response) {
      if (response && response.sections && response.sections['cart-icon-bubble']) {
        // Use centralized method if available, otherwise fallback to direct update
        if (window.MarketCartUtils && window.MarketCartUtils.updateCartIconBubble) {
          window.MarketCartUtils.updateCartIconBubble(response.sections['cart-icon-bubble']);
        } else {
          // Fallback for when MarketCartUtils is not loaded
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            const tmpl = document.createElement('template');
            tmpl.innerHTML = response.sections['cart-icon-bubble'];
            const sectionWrapper = tmpl.content.querySelector('.shopify-section');
            cartIconBubble.innerHTML = sectionWrapper ? sectionWrapper.innerHTML : response.sections['cart-icon-bubble'];
          }
        }
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
